import { api } from '../services/api';
import { GPS_MAX_FIX_AGE_MS, assessGpsFix } from './gpsQuality';

const STORAGE_KEY = 'oh_gps_offline_queue_v1';
const MAX_ITEMS = 5000;
/** Ubicación "en vivo" no se reenvía si el punto ya es demasiado viejo (evita pin en casa). */
const LIVE_UBICACION_MAX_AGE_MS = Math.max(GPS_MAX_FIX_AGE_MS, 2 * 60 * 1000);
let flushing = null;

function readQueue() {
  try {
    const rows = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(rows) ? rows : [];
  } catch {
    return [];
  }
}

function writeQueue(rows) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rows.slice(-MAX_ITEMS)));
  } catch {
    // El GPS nunca debe bloquear la toma del pedido si el dispositivo no tiene espacio.
  }
}

export function newGpsId(prefix = 'gps') {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function enqueueGpsMutation(type, data) {
  let rows = readQueue();
  const id = data?.payload?.client_point_id || newGpsId(type);
  if (type === 'iniciar' && data?.sucursalId != null) {
    rows = rows.filter((row) => !(row.type === 'iniciar' && row.sucursalId === data.sucursalId));
  }
  if (rows.some((row) => row.id === id)) return id;
  rows.push({
    id,
    type,
    ...data,
    created_at: new Date().toISOString(),
    attempts: 0,
  });
  writeQueue(rows);
  return id;
}

function removeItem(id) {
  writeQueue(readQueue().filter((row) => row.id !== id));
}

function updateItem(id, patch) {
  writeQueue(readQueue().map((row) => (row.id === id ? { ...row, ...patch } : row)));
}

function payloadAgeMs(payload, queuedAt) {
  if (payload?.client_ts) {
    const t = new Date(payload.client_ts).getTime();
    if (Number.isFinite(t)) return Date.now() - t;
  }
  if (queuedAt) {
    const t = new Date(queuedAt).getTime();
    if (Number.isFinite(t)) return Date.now() - t;
  }
  return 0;
}

/** true = descartar sin enviar (no bloquear cola). */
function shouldDropStaleLive(row) {
  if (row.type !== 'ubicacion') return false;
  const payload = row.payload || {};
  const assessed = assessGpsFix({
    lat: payload.lat,
    lng: payload.lng,
    accuracy: payload.accuracy,
    client_ts: payload.client_ts,
  });
  if (!assessed.ok && (assessed.reason === 'accuracy' || assessed.reason === 'coords')) {
    return true;
  }
  return payloadAgeMs(payload, row.created_at) > LIVE_UBICACION_MAX_AGE_MS;
}

async function send(row) {
  if (row.type === 'ubicacion') return api.enviarUbicacionVendedor(row.payload);
  if (row.type === 'punto') return api.registrarPuntoVisita(row.visitaId, row.payload);
  if (row.type === 'cerrar') return api.cerrarVisita(row.visitaId, row.payload);
  if (row.type === 'iniciar') {
    const res = await api.iniciarVisita(row.payload);
    return { visita: res.data, row };
  }
  throw Object.assign(new Error('Tipo GPS no válido'), { status: 400 });
}

function notifyIniciarSuccess(visita, row, onIniciarSuccess) {
  if (onIniciarSuccess) onIniciarSuccess(visita, row);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('oh-visita-iniciada', { detail: { visita, row } })
    );
  }
}

export async function flushGpsQueue({ onIniciarSuccess } = {}) {
  if (flushing) return flushing;
  flushing = (async () => {
    const rows = readQueue();
    let sent = 0;
    for (const row of rows) {
      try {
        if (shouldDropStaleLive(row)) {
          removeItem(row.id);
          continue;
        }
        const result = await send(row);
        if (row.type === 'iniciar' && result?.visita) {
          notifyIniciarSuccess(result.visita, row, onIniciarSuccess);
        }
        removeItem(row.id);
        sent += 1;
      } catch (err) {
        const attempts = Number(row.attempts || 0) + 1;
        // Un punto viejo o una visita inexistente no debe bloquear toda la cola.
        if (err?.status >= 400 && err.status < 500 && err.status !== 429) {
          removeItem(row.id);
          continue;
        }
        updateItem(row.id, { attempts, last_error: err?.message || 'Error de sincronización' });
        break;
      }
    }
    return { sent, remaining: readQueue().length };
  })().finally(() => {
    flushing = null;
  });
  return flushing;
}

export function gpsQueueCount() {
  return readQueue().length;
}
